import { FileText } from "lucide-react";
import { formatDateTime } from "@/design/format";
import type { TraceResult } from "@/domain/trace";

type EvidenceItem = TraceResult["evidence"][number];

/**
 * Evidence is terminal at leaves (Trace Source spec §3.5.4/§3.5.6) — photos,
 * geo-tag, notes and supplier documents render inline here, never a
 * "drill further" pointer. Replaces the old "Held further down" dead end.
 */
export function EvidenceInline({ item }: { item: EvidenceItem }) {
  const isPhoto = item.type === "photo" && !!item.fileUrl;
  return (
    <div className="mb-2.5 grid grid-cols-[120px_1fr] gap-3.5 rounded-lg bg-subtle p-3">
      {isPhoto ? (
        <img
          src={item.fileUrl}
          alt={item.note || item.filename || "Evidence photo"}
          loading="lazy"
          className="h-[88px] w-[120px] rounded-md object-cover"
        />
      ) : (
        // brand/brandHover are bare var(--x) tokens (tailwind.config.ts) — Tailwind
        // can't compute an opacity modifier against a color it can't statically
        // parse, so `from-brand/70` would silently generate no CSS at all (the
        // same bug fixed for the ProjectSummaryPage hero earlier). Two solid
        // stops avoid it entirely.
        <div className="flex h-[88px] w-[120px] flex-col items-center justify-center gap-1 rounded-md bg-gradient-to-br from-brand to-brandHover text-onDark">
          <FileText className="h-5 w-5" aria-hidden />
          <span className="max-w-full truncate px-1.5 font-mono text-[10px]">{item.filename ?? item.type}</span>
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate font-mono text-[11.5px] font-medium text-ink">{item.filename ?? item.label}</p>
        <p className="mt-0.5 text-[11.5px] text-faint">
          {[
            item.type === "photo" ? "Photo" : item.type === "pdf" ? "Document" : "Document",
            item.geo ? `${item.geo.lat.toFixed(3)}, ${item.geo.lng.toFixed(3)}` : null,
            item.capturedAt ? `captured ${formatDateTime(item.capturedAt)}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {item.note && (
          <p className="mt-2 rounded-r border-l-2 border-brand bg-card py-1.5 pl-2.5 pr-2.5 text-xs text-mute">
            &ldquo;{item.note}&rdquo;
          </p>
        )}
      </div>
    </div>
  );
}
